/**
 * Service for managing pipeline configurations.
 */
import supabase from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export interface Pipeline {
  id: string;
  user_id: string;
  name: string;
  display_name?: string;
  description?: string;
  s3_base_path?: string;  // Changed from s3_path to match backend
  s3_path?: string;  // Keep for backward compatibility
  pipeline_config?: any;
  created_at?: string;
  updated_at?: string;
}

export interface SavePipelineRequest {
  name: string;
  pipeline: any;
  description?: string;
}

class PipelineService {
  /**
   * Get authentication headers.
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }

  /**
   * List all pipelines for the current user.
   */
  async listPipelines(): Promise<Pipeline[]> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/pipelines`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list pipelines');
    }

    const data = await response.json();
    console.log('[PipelineService] Response:', data);
    return data.pipelines || [];
  }

  /**
   * Get a specific pipeline by ID.
   */
  async getPipeline(pipelineId: string): Promise<{ pipeline: Pipeline; pipeline_json: any }> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/pipelines/${pipelineId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get pipeline');
    }

    return response.json();
  }

  /**
   * Save a new pipeline.
   */
  async savePipeline(request: SavePipelineRequest): Promise<Pipeline> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/pipelines`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save pipeline');
    }

    const data = await response.json();
    return data;
  }

  /**
   * Delete a pipeline.
   */
  async deletePipeline(pipelineId: string): Promise<void> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/pipelines/${pipelineId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete pipeline');
    }
  }
}

export const pipelineService = new PipelineService();
export default pipelineService;

